/**
 * Multi-account "Switch user". Lists the accounts signed in on THIS device
 * (from /api/auth/accounts), and switches / adds / signs-out among them.
 *
 * Switching re-points the active session cookies server-side (no re-auth) and
 * then does a FULL document reload so the SPA re-initialises auth + per-account
 * state cleanly for the newly-active session — the same hard-navigation
 * rationale as the login flow (a soft nav would race the stale useAuth cache).
 *
 * State is module-shared via useState so the menu and any other surface stay
 * in sync after a sign-out/add.
 */
export type SwitchAccount = {
  sid: string
  active: boolean
  name: string | null
  email: string | null
  roles: string[]
  isAdmin: boolean
  trusted: boolean
  createdAt: number | null
  expiresAt: number | null
}

export function useAccountSwitcher() {
  const accounts = useState<SwitchAccount[]>('account-switcher:list', () => [])
  const loading = useState<boolean>('account-switcher:loading', () => false)
  const { csrf } = useAuth()
  const toast = useToast()
  const { start: startScreenLoader, stop: stopScreenLoader } = useScreenLoader()

  // The selected household + sidebar caches live in per-tab sessionStorage
  // (pinia-plugin-persistedstate, keyed by the 'tab-session' store id). Drop
  // them when the active USER changes so the next account doesn't momentarily
  // render the previous account's household/selection before its data loads.
  function clearTabState() {
    try {
      if (import.meta.client) {
        sessionStorage.removeItem('tab-session')
        // Also drop the "View as" preview (persisted under the 'view-as' store
        // id). It belongs to the OUTGOING user's session — leaving it makes the
        // incoming account inherit the preview, and switching BACK to an admin
        // RE-ACTIVATES the stale downgrade (canViewAs is true again) so
        // hasDisplayRole is non-admin → the admin middleware 403s every /admin/*
        // route (the "forbidden after switching back" bug).
        sessionStorage.removeItem('view-as')
      }
    } catch { /* ignore */ }
  }

  async function load() {
    loading.value = true
    try {
      const r: any = await $fetch('/api/auth/accounts', { credentials: 'include' })
      accounts.value = r?.accounts || []
    } catch {
      accounts.value = []
    } finally {
      loading.value = false
    }
  }

  /** Add another account: a normal sign-in that JOINS this device's set. */
  function addAccount() {
    clearTabState() // don't carry this user's household/view-as into the new sign-in
    window.location.href = '/login?add=1'
  }

  async function switchTo(sid: string) {
    startScreenLoader('Switching account…')
    try {
      const res: any = await $fetch('/api/auth/accounts/switch', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf': csrf.value || '' },
        body: { sid }
      })
      if (res?.stepUpRequired) {
        stopScreenLoader()
        // Policy: switching INTO an Admin needs a fresh sign-in. Route them to
        // re-authenticate that account (it rejoins the device set on success).
        // Clear first — this is the exact path back into the admin, so the stale
        // preview must not survive the re-auth.
        clearTabState()
        toast.add({
          title: 'Sign in to continue',
          description: 'Switching to this account requires you to sign in again.',
          color: 'info'
        })
        window.location.href = '/login?add=1'
        return
      }
      clearTabState()
      window.location.href = res?.redirectTo || '/'
    } catch (e: any) {
      stopScreenLoader()
      toast.add({
        title: 'Could not switch account',
        description: e?.data?.statusMessage || e?.statusMessage || 'Please try again.',
        color: 'error'
      })
    }
  }

  /** Sign out one account. Active → navigate to next/login; background → just refresh the list. */
  async function signOut(sid: string) {
    try {
      const res: any = await $fetch('/api/auth/accounts/remove', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf': csrf.value || '' },
        body: { sid }
      })
      if (res?.redirectTo) {
        clearTabState()
        window.location.href = res.redirectTo
        return
      }
      await load()
    } catch (e: any) {
      toast.add({
        title: 'Could not sign out',
        description: e?.data?.statusMessage || e?.statusMessage || 'Please try again.',
        color: 'error'
      })
    }
  }

  /** Sign out every account on this device. */
  async function signOutAll() {
    await useLogout()
  }

  const activeAccount = computed(() => accounts.value.find(a => a.active) || null)

  return { accounts, activeAccount, loading, load, addAccount, switchTo, signOut, signOutAll }
}
